import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users,
  Copy,
  Search,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  BarChart3,
  Award,
  Target,
  Percent,
  Lock,
  Unlock
} from 'lucide-react';

interface StakingPool {
  id: string;
  name: string;
  validator: string;
  validatorName?: string;
  totalStaked: string;
  apy: number;
  commission: number;
  minStake: string;
  maxStake?: string;
  lockPeriod: number; // days
  status: 'active' | 'inactive' | 'full';
  participants: number;
  rewards24h: string;
  totalRewards: string;
  createdAt: string;
  type: 'validator' | 'liquid' | 'fixed';
}

interface UserStake {
  poolId: string;
  poolName: string;
  stakedAmount: string;
  rewards: string;
  apy: number;
  stakingDate: string;
  unlockDate?: string;
  status: 'active' | 'unstaking' | 'unlocked';
}

interface StakingStats {
  totalStaked: string;
  totalRewards: string;
  averageApy: number;
  activePools: number;
  totalStakers: number;
}

const StakingPage: React.FC = () => {
  const [pools, setPools] = useState<StakingPool[]>([]);
  const [userStakes, setUserStakes] = useState<UserStake[]>([]);
  const [stats, setStats] = useState<StakingStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('apy');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'pools' | 'mystakes'>('pools');
  const poolsPerPage = 12;
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [selectedPool, setSelectedPool] = useState<StakingPool | null>(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [showCreatePoolModal, setShowCreatePoolModal] = useState(false);
  const [newPool, setNewPool] = useState({
    name: '',
    minStake: '',
    commission: 5,
    lockPeriod: 30,
    type: 'validator' as 'validator' | 'liquid' | 'fixed'
  });

  useEffect(() => {
    fetchStakingData();
    const interval = setInterval(fetchStakingData, 30000); // 30초마다 업데이트
    return () => clearInterval(interval);
  }, [filterType, filterStatus, sortBy]);

  const fetchStakingData = async () => {
    try {
      setLoading(true);
      
      // 실제 데이터베이스에서 스테이킹 풀 데이터 가져오기
      const response = await fetch('http://localhost:3600/api/staking/pools', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      let mockPools: StakingPool[] = [];
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.pools) {
          mockPools = data.pools.map((pool: any) => ({
            id: pool.id.toString(),
            name: pool.name,
            validator: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
            validatorName: 'VTTN Validator',
            totalStaked: pool.total_staked ? pool.total_staked.toString() : '0',
            apy: parseFloat(pool.apy) * 100, // 백분율로 변환
            commission: 5,
            minStake: pool.min_stake.toString(),
            maxStake: pool.max_stake ? pool.max_stake.toString() : (parseFloat(pool.min_stake) * 1000).toString(),
            lockPeriod: pool.lock_period * 30, // 개월을 일로 변환
            status: pool.is_active ? 'active' : 'inactive',
            participants: pool.participants_count || 0,
            rewards24h: '0.00',
            totalRewards: '0',
            createdAt: pool.created_at,
            type: 'validator'
          }));
        }
      }
      
      // API 실패 시 기본 데이터 (VTTN 스테이킹 풀 1개만)
      if (mockPools.length === 0) {
        mockPools = [
          {
            id: 'pool-1',
            name: 'VTTN 스테이킹 풀',
            validator: '0x742d35Cc6634C0532925a3b8D4C2C2C2C2C2C2C2',
            validatorName: 'VTTN Validator',
            totalStaked: '0',
            apy: 16.0,
            commission: 5,
            minStake: '1000',
            maxStake: '1000000',
            lockPeriod: 180, // 6개월
            status: 'active',
            participants: 0,
            rewards24h: '0.00',
            totalRewards: '0',
            createdAt: new Date().toISOString(),
            type: 'validator'
          }
        ];
      }
      
      // 사용자 스테이킹 데이터 (현재는 빈 배열)
      const mockUserStakes: UserStake[] = [];
      
      // 필터링
      let filteredPools = mockPools;
      
      if (filterType !== 'all') {
        filteredPools = filteredPools.filter(pool => pool.type === filterType);
      }
      
      if (filterStatus !== 'all') {
        filteredPools = filteredPools.filter(pool => pool.status === filterStatus);
      }
      
      if (searchTerm) {
        filteredPools = filteredPools.filter(pool => 
          pool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (pool.validatorName && pool.validatorName.toLowerCase().includes(searchTerm.toLowerCase())) ||
          pool.validator.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // 정렬
      filteredPools.sort((a, b) => {
        switch (sortBy) {
          case 'apy':
            return b.apy - a.apy;
          case 'totalStaked':
            return parseFloat(b.totalStaked) - parseFloat(a.totalStaked);
          case 'participants':
            return b.participants - a.participants;
          case 'rewards':
            return parseFloat(b.rewards24h) - parseFloat(a.rewards24h);
          default:
            return b.apy - a.apy;
        }
      });
      
      setPools(filteredPools);
      setUserStakes(mockUserStakes);
      
      // 통계 계산
      const totalStaked = filteredPools.reduce((sum, pool) => sum + parseFloat(pool.totalStaked), 0);
      const totalRewards = filteredPools.reduce((sum, pool) => sum + parseFloat(pool.totalRewards), 0);
      const averageApy = filteredPools.reduce((sum, pool) => sum + pool.apy, 0) / filteredPools.length;
      const activePools = filteredPools.filter(pool => pool.status === 'active').length;
      const totalStakers = filteredPools.reduce((sum, pool) => sum + pool.participants, 0);
      
      setStats({
        totalStaked: totalStaked.toFixed(0),
        totalRewards: totalRewards.toFixed(0),
        averageApy: averageApy,
        activePools: activePools,
        totalStakers: totalStakers
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch staking data:', error);
      setLoading(false);
    }
  };

  const formatNumber = (num: number | string) => {
    const n = typeof num === 'string' ? parseFloat(num) : num;
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
    return n.toFixed(0);
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="w-4 h-4 text-gray-500" />;
      case 'full':
        return <AlertCircle className="w-4 h-4 text-yellow-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'full': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'validator': return 'bg-purple-100 text-purple-800';
      case 'liquid': return 'bg-blue-100 text-blue-800';
      case 'fixed': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTimeAgo = (timestamp: string) => {
    const seconds = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const paginatedPools = pools.slice(
    (currentPage - 1) * poolsPerPage,
    currentPage * poolsPerPage
  );

  const totalPages = Math.ceil(pools.length / poolsPerPage);

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* 헤더 */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold gradient-text mb-2">Staking</h1>
          <p className="text-slate-600">Stake VTTN tokens and earn rewards</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowCreatePoolModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
          >
            <Target className="w-4 h-4" />
            <span>Create Pool</span>
          </button>
          <button
            onClick={fetchStakingData}
            className="flex items-center space-x-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* 통계 카드 */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Staked</p>
                <p className="text-2xl font-bold text-purple-600">{formatNumber(stats.totalStaked)} VTTN</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Rewards</p>
                <p className="text-2xl font-bold text-green-600">{formatNumber(stats.totalRewards)} VTTN</p>
              </div>
              <Award className="w-8 h-8 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Average APY</p>
                <p className="text-2xl font-bold text-blue-600">{stats.averageApy.toFixed(1)}%</p>
              </div>
              <Percent className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Pools</p>
                <p className="text-2xl font-bold text-orange-600">{stats.activePools}</p>
              </div>
              <Target className="w-8 h-8 text-orange-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Stakers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalStakers.toLocaleString()}</p>
              </div>
              <Users className="w-8 h-8 text-gray-500" />
            </div>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('pools')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'pools'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Staking Pools
          </button>
          <button
            onClick={() => setActiveTab('mystakes')}
            className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'mystakes'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            My Stakes
          </button>
        </div>
      </div>

      {activeTab === 'pools' && (
        <>
          {/* 검색 및 필터 */}
          <div className="bg-white rounded-xl p-6 border border-gray-200">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by pool name or validator..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="validator">Validator</option>
                <option value="liquid">Liquid</option>
                <option value="fixed">Fixed Term</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="full">Full</option>
              </select>
              
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                <option value="apy">APY</option>
                <option value="totalStaked">Total Staked</option>
                <option value="participants">Participants</option>
                <option value="rewards">Rewards</option>
              </select>
            </div>
          </div>

          {/* 스테이킹 풀 그리드 */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded mb-4"></div>
                  <div className="space-y-2">
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))
            ) : paginatedPools.length === 0 ? (
              <div className="col-span-full text-center py-12 text-gray-500">
                No staking pools found
              </div>
            ) : (
              paginatedPools.map((pool) => (
                <div key={pool.id} className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{pool.name}</h3>
                      {pool.validatorName && (
                        <p className="text-sm text-gray-600">{pool.validatorName}</p>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(pool.status)}
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(pool.status)}`}>
                        {pool.status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">APY</span>
                      <span className="text-lg font-bold text-green-600">{pool.apy.toFixed(1)}%</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Total Staked</span>
                      <span className="text-sm font-medium text-gray-900">{formatNumber(pool.totalStaked)} VTTN</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Commission</span>
                      <span className="text-sm font-medium text-gray-900">{pool.commission}%</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Min Stake</span>
                      <span className="text-sm font-medium text-gray-900">{formatNumber(pool.minStake)} VTTN</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Lock Period</span>
                      <span className="text-sm font-medium text-gray-900">
                        {pool.lockPeriod === 0 ? 'Liquid' : `${pool.lockPeriod} days`}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">Participants</span>
                      <span className="text-sm font-medium text-gray-900">{pool.participants.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(pool.type)}`}>
                      {pool.type}
                    </span>
                    <span className="text-xs text-gray-500">
                      Created {formatTimeAgo(pool.createdAt)}
                    </span>
                  </div>
                  
                  <button
                    className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                      pool.status === 'active'
                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    disabled={pool.status !== 'active'}
                  >
                    {pool.status === 'active' ? 'Stake Now' : 
                     pool.status === 'full' ? 'Pool Full' : 'Inactive'}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                if (page > totalPages) return null;
                
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-lg ${
                      currentPage === page
                        ? 'bg-orange-500 text-white border-orange-500'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {activeTab === 'mystakes' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">My Staking Positions</h3>
            
            {userStakes.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Lock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No staking positions found</p>
                <p className="text-sm">Start staking to earn rewards!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {userStakes.map((stake, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-medium text-gray-900">{stake.poolName}</h4>
                        <p className="text-sm text-gray-600">Staked on {new Date(stake.stakingDate).toLocaleDateString()}</p>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        stake.status === 'active' ? 'bg-green-100 text-green-800' :
                        stake.status === 'unstaking' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {stake.status}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-sm text-gray-600">Staked Amount</p>
                        <p className="font-medium text-gray-900">{formatNumber(stake.stakedAmount)} VTTN</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Rewards Earned</p>
                        <p className="font-medium text-green-600">{stake.rewards} VTTN</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">APY</p>
                        <p className="font-medium text-blue-600">{stake.apy.toFixed(1)}%</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-medium text-gray-900">
                          {stake.unlockDate ? (
                            new Date(stake.unlockDate) > new Date() ? (
                              `Locked until ${new Date(stake.unlockDate).toLocaleDateString()}`
                            ) : (
                              'Unlocked'
                            )
                          ) : (
                            'Liquid'
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex space-x-3">
                      <button className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors">
                        Claim Rewards
                      </button>
                      <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                        {stake.unlockDate && new Date(stake.unlockDate) > new Date() ? 'Request Unstake' : 'Unstake'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {/* 스테이킹 풀 생성 모달 */}
      {showCreatePoolModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Create Staking Pool</h2>
              <button
                onClick={() => setShowCreatePoolModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <form className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pool Name
                </label>
                <input
                  type="text"
                  value={newPool.name}
                  onChange={(e) => setNewPool({ ...newPool, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter pool name..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pool Type
                </label>
                <select
                  value={newPool.type}
                  onChange={(e) => setNewPool({ ...newPool, type: e.target.value as 'validator' | 'liquid' | 'fixed' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="validator">Validator Pool</option>
                  <option value="liquid">Liquid Staking</option>
                  <option value="fixed">Fixed Term</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum Stake (VTTN)
                  </label>
                  <input
                    type="number"
                    value={newPool.minStake}
                    onChange={(e) => setNewPool({ ...newPool, minStake: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Commission (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="20"
                    value={newPool.commission}
                    onChange={(e) => setNewPool({ ...newPool, commission: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Lock Period (days)
                </label>
                <select
                  value={newPool.lockPeriod}
                  onChange={(e) => setNewPool({ ...newPool, lockPeriod: parseInt(e.target.value) })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value={0}>No Lock</option>
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={180}>180 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-purple-800">Pool Creation Requirements</h4>
                    <ul className="text-sm text-purple-700 mt-1 space-y-1">
                      <li>• Minimum 10,000 VTTN required as initial stake</li>
                      <li>• Pool creation fee: 100 VTTN</li>
                      <li>• Must maintain validator node uptime &gt; 95%</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setShowCreatePoolModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  Create Pool
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* 스테이킹 참여 모달 */}
      {showStakeModal && selectedPool && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Stake to {selectedPool.name}</h2>
              <button
                onClick={() => setShowStakeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">APY:</span>
                    <span className="font-medium text-green-600 ml-2">{selectedPool.apy}%</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Commission:</span>
                    <span className="font-medium ml-2">{selectedPool.commission}%</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Min Stake:</span>
                    <span className="font-medium ml-2">{formatNumber(selectedPool.minStake)} VTTN</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Lock Period:</span>
                    <span className="font-medium ml-2">{selectedPool.lockPeriod} days</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Stake Amount (VTTN)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={stakeAmount}
                    onChange={(e) => setStakeAmount(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter amount to stake..."
                  />
                  <button
                    onClick={() => setStakeAmount(walletBalance.toString())}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-600 text-sm font-medium hover:text-purple-700"
                  >
                    MAX
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Available: {walletBalance.toLocaleString()} VTTN
                </p>
              </div>

              {stakeAmount && parseFloat(stakeAmount) > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-green-800 mb-2">Estimated Rewards</h4>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Daily:</span>
                      <span>{((parseFloat(stakeAmount) * selectedPool.apy / 100) / 365).toFixed(4)} VTTN</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Monthly:</span>
                      <span>{((parseFloat(stakeAmount) * selectedPool.apy / 100) / 12).toFixed(2)} VTTN</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Yearly:</span>
                      <span>{(parseFloat(stakeAmount) * selectedPool.apy / 100).toFixed(2)} VTTN</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex space-x-4">
                <button
                  onClick={() => setShowStakeModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                >
                  Stake Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default StakingPage;
